import React, { useState } from 'react';
import { getStringValue } from '../../utils/blockHelpers';

const ArticlePreview = ({ title, blocks, authorName, headerImage }) => {
  const [selectedStatCategory, setSelectedStatCategory] = useState({});

  const renderPreviewBlock = (block) => {
    if (block.type === 'maxpreps') {
      console.log('[ArticlePreview] Rendering maxpreps block at renderPreviewBlock, block:', block);
    }
    switch (block.type) {
      case 'text':
        const level = block.formatting?.level || 'p';
        const formatting = block.formatting || {};
        let content = block.content;
        if (formatting.isBold || formatting.isItalic || formatting.isUnderline) {
          content = (
            <span
              style={{
                fontWeight: formatting.isBold ? 'bold' : 'normal',
                fontStyle: formatting.isItalic ? 'italic' : 'normal',
                textDecoration: formatting.isUnderline ? 'underline' : 'none',
              }}
            >
              {block.content}
            </span>
          );
        }
        const baseStyle = {
          fontSize: level === 'h1' ? '2em' : level === 'h2' ? '1.5em' : level === 'h3' ? '1.25em' : 'inherit',
          fontWeight: level.startsWith('h') ? 'bold' : 'normal',
          marginBottom: '1rem',
        };
        return React.createElement(level, { key: block.id, style: baseStyle }, content);

      case 'list-ordered':
        const lines = (block.content || '').split('\n');
        return (
          <ol key={block.id} style={{ marginBottom: '1rem', paddingLeft: '2rem' }} className="content-list">
            {lines.map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
          </ol>
        );

      case 'list-unordered':
        const ulines = (block.content || '').split('\n');
        return (
          <ul key={block.id} style={{ marginBottom: '1rem', paddingLeft: '2rem' }} className="content-list">
            {ulines.map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
          </ul>
        );

      case 'image':
        return (
          <div key={block.id} className="preview-image-block" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <img
              src={block.src}
              alt={block.alt || 'Image'}
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'inline-block',
              }}
            />
            {block.caption && (
              <p style={{ marginTop: '0.5rem', fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
                {block.caption}
              </p>
            )}
          </div>
        );

      case 'video':
        return (
          <div key={block.id} className="preview-video-block">
            <iframe
              width="100%"
              height="400"
              src={block.src}
              title={block.title || 'Video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ marginBottom: '1rem' }}
            />
          </div>
        );

      case 'separator':
        return <hr key={block.id} className="preview-separator" />;

      case 'player-comparison':
        const statCategories = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'BLOCKING', 'KICKING'];
        const p1Stats = block.player1_stats || {};
        const p2Stats = block.player2_stats || {};

        return (
          <div key={block.id} className="preview-player-comparison" style={{ marginBottom: '2rem' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '1rem' }}>
              {getStringValue(block.player1_name)} vs {getStringValue(block.player2_name)}
            </h3>
            <div style={{ overflowX: 'auto' }}>
              {statCategories.map((category) => {
                if (!p1Stats[category] && !p2Stats[category]) return null;
                const p1Data = p1Stats[category];
                const p2Data = p2Stats[category];

                const getStatKeys = () => {
                  if (p1Data) return Object.keys(p1Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID' && k !== 'POS');
                  if (p2Data) return Object.keys(p2Data).filter(k => k !== 'NAME' && k !== 'TEAM' && k !== 'ID' && k !== 'POS');
                  return [];
                };

                const statKeys = getStatKeys();

                return (
                  <div key={category} style={{ marginBottom: '2rem' }}>
                    <h4 style={{ backgroundColor: '#003366', color: 'white', padding: '0.5rem', textAlign: 'center' }}>
                      {category}
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                            {getStringValue(block.player1_name)}
                          </th>
                          <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                            Stat
                          </th>
                          <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                            {getStringValue(block.player2_name)}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {statKeys.map((statName) => {
                          const v1 = p1Data?.[statName];
                          const v2 = p2Data?.[statName];
                          const n1 = parseFloat(v1);
                          const n2 = parseFloat(v2);
                          const isP1Higher = !isNaN(n1) && !isNaN(n2) && n1 > n2;
                          const isP2Higher = !isNaN(n1) && !isNaN(n2) && n2 > n1;

                          const formatValue = (val) => {
                            if (val === null || val === undefined) return '-';
                            if (typeof val === 'number') return val.toFixed(2);
                            if (typeof val === 'object') return getStringValue(val);
                            return val;
                          };

                            return (
                              <tr key={statName} style={{ borderBottom: '1px solid #eee' }}>
                                <td
                                  style={{
                                    padding: '0.75rem',
                                    fontWeight: isP1Higher ? 'bold' : 'normal',
                                    color: isP1Higher ? '#003366' : 'inherit',
                                  }}
                                >
                                  {formatValue(v1)}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', color: '#666' }}>
                                  {statName}
                                </td>
                                <td
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'right',
                                    fontWeight: isP2Higher ? 'bold' : 'normal',
                                    color: isP2Higher ? '#003366' : 'inherit',
                                  }}
                                >
                                  {formatValue(v2)}
                                </td>
                              </tr>
                            );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'teamcomparison':
        const statCats = ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'BLOCKING', 'KICKING'];
        const t1Stats = block.team1_stats || {};
        const t2Stats = block.team2_stats || {};

        return (
          <div key={block.id} className="preview-team-comparison" style={{ marginBottom: '2rem' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '1rem' }}>
              {getStringValue(block.team1_name)} vs {getStringValue(block.team2_name)}
            </h3>
            <div style={{ overflowX: 'auto' }}>
              {statCats.map((category) => {
                if (!t1Stats[category] && !t2Stats[category]) return null;
                const t1Data = t1Stats[category];
                const t2Data = t2Stats[category];

                const getStatKeys = () => {
                  const excluded = new Set(['TEAM', 'team', 'PLAYER_COUNT', 'NAME', 'name', 'POS', 'pos']);
                  const keys = new Set();
                  if (t1Data && typeof t1Data === 'object') {
                    Object.keys(t1Data).forEach(k => {
                      if (!excluded.has(k)) keys.add(k);
                    });
                  }
                  if (t2Data && typeof t2Data === 'object') {
                    Object.keys(t2Data).forEach(k => {
                      if (!excluded.has(k)) keys.add(k);
                    });
                  }
                  return Array.from(keys).sort();
                };

                const statKeys = getStatKeys();

                return (
                  <div key={category} style={{ marginBottom: '2rem' }}>
                    <h4 style={{ backgroundColor: '#003366', color: 'white', padding: '0.5rem', textAlign: 'center' }}>
                      {category}
                    </h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                            {getStringValue(block.team1_name)}
                          </th>
                          <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>
                            Stat
                          </th>
                          <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                            {getStringValue(block.team2_name)}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {statKeys.map((statName) => {
                          const v1 = t1Data?.[statName];
                          const v2 = t2Data?.[statName];
                          const n1 = parseFloat(v1);
                          const n2 = parseFloat(v2);
                          const isT1Higher = !isNaN(n1) && !isNaN(n2) && n1 > n2;
                          const isT2Higher = !isNaN(n1) && !isNaN(n2) && n2 > n1;

                          const formatValue = (val) => {
                            if (val === null || val === undefined) return '-';
                            if (typeof val === 'number') return val.toFixed(2);
                            if (typeof val === 'object') return getStringValue(val);
                            return val;
                          };

                            return (
                              <tr key={statName} style={{ borderBottom: '1px solid #eee' }}>
                                <td
                                  style={{
                                    padding: '0.75rem',
                                    fontWeight: isT1Higher ? 'bold' : 'normal',
                                    color: isT1Higher ? '#003366' : 'inherit',
                                  }}
                                >
                                  {formatValue(v1)}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.85rem', color: '#666' }}>
                                  {statName}
                                </td>
                                <td
                                  style={{
                                    padding: '0.75rem',
                                    textAlign: 'right',
                                    fontWeight: isT2Higher ? 'bold' : 'normal',
                                    color: isT2Higher ? '#003366' : 'inherit',
                                  }}
                                >
                                  {formatValue(v2)}
                                </td>
                              </tr>
                            );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'maxpreps':
        console.log('[ArticlePreview] maxpreps block:', { hasGameData: !!block.gameData, hasTeamStats: !!block.teamStats, blockKeys: Object.keys(block), block });
        if (!block.gameData || !block.teamStats) {
          return (
            <div key={block.id} className="preview-maxpreps" style={{ 
              marginBottom: '2rem', 
              padding: '1.5rem', 
              backgroundColor: '#f0f4f8', 
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}>
              <div style={{ textAlign: 'center', color: '#666' }}>
                <h3 style={{ margin: '0.5rem 0', color: '#333' }}>📊 MaxPreps Game Details Block</h3>
                <p>
                  Select a week and game to preview game statistics, final scores, team stats comparison, and top performers.
                </p>
              </div>
            </div>
          );
        }
        
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
              <h4 style={{ marginTop: 0 }}>Final Score</h4>
              {block.gameData && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
                  <div>{getStringValue(block.gameData.team1?.name)}</div>
                  <div>{block.finalScore || 'Game Scheduled'}</div>
                  <div>{getStringValue(block.gameData.team2?.name)}</div>
                </div>
              )}
            </div>
            <div style={{ marginBottom: '2rem' }}>
              {block.teamStats.team1 && block.teamStats.team2 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <h5 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>{getStringValue(block.teamStats.team1.name)}</h5>
                    <div style={{ fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                        <span>Wins:</span>
                        <strong>{block.teamStats.team1.stats?.wins || 0}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                        <span>Losses:</span>
                        <strong>{block.teamStats.team1.stats?.losses || 0}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem' }}>
                        <span>Points For:</span>
                        <strong>{block.teamStats.team1.stats?.points_for || 0}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Points Against:</span>
                        <strong>{block.teamStats.team1.stats?.points_against || 0}</strong>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                    <h5 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>{getStringValue(block.teamStats.team2.name)}</h5>
                    <div style={{ fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                        <span>Wins:</span>
                        <strong>{block.teamStats.team2.stats?.wins || 0}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                        <span>Losses:</span>
                        <strong>{block.teamStats.team2.stats?.losses || 0}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem' }}>
                        <span>Points For:</span>
                        <strong>{block.teamStats.team2.stats?.points_for || 0}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Points Against:</span>
                        <strong>{block.teamStats.team2.stats?.points_against || 0}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {block.allStats && Object.keys(block.allStats).length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '2px solid #333', overflow: 'auto' }}>
                    {['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'KICKING', 'BLOCKING'].map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedStatCategory({ ...selectedStatCategory, [block.id]: category })}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: (selectedStatCategory[block.id] || 'PASSING') === category ? '#00509e' : 'transparent',
                          color: (selectedStatCategory[block.id] || 'PASSING') === category ? 'white' : '#666',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#003d7a', color: 'white' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #333' }}>{getStringValue(block.teamStats.team1.name)}</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #333' }}>STAT</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '2px solid #333' }}>{getStringValue(block.teamStats.team2.name)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const currentCategory = selectedStatCategory[block.id] || 'PASSING';
                        const categoryStats = block.allStats[currentCategory] || [];
                        const statKeys = ['PYD', 'CMP', 'ATT', 'TD', 'INT', 'YARDS', 'TD', 'ATT', 'YDS', 'AVG', 'REC', 'YDS', 'AVG', 'TD', 'SOLO', 'AST', 'TFL', 'SACK', 'FF', 'FR', 'INT', 'PD', 'XPA', 'XPM', 'FGA', 'FGM'];
                        const team1Players = categoryStats.filter(p => p.TEAM === block.teamStats.team1.name) || [];
                        const team2Players = categoryStats.filter(p => p.TEAM === block.teamStats.team2.name) || [];
                        
                        const getStatValue = (players, statKey) => {
                          if (!players || players.length === 0) return '-';
                          const value = players.reduce((sum, p) => sum + (parseFloat(p[statKey]) || 0), 0) / players.length;
                          return isNaN(value) ? '-' : value.toFixed(2);
                        };
                        
                        const displayStats = currentCategory === 'PASSING' ? ['PYD', 'CMP', 'ATT', 'TD', 'INT'] 
                          : currentCategory === 'RUSHING' ? ['YARDS', 'ATT', 'AVG', 'TD']
                          : currentCategory === 'RECEIVING' ? ['REC', 'YDS', 'AVG', 'TD']
                          : currentCategory === 'DEFENSE' ? ['SOLO', 'AST', 'TFL', 'SACK', 'INT']
                          : currentCategory === 'KICKING' ? ['FGA', 'FGM', 'XPA', 'XPM']
                          : currentCategory === 'BLOCKING' ? ['PLY'] : [];
                        
                        return displayStats.map((stat, idx) => (
                          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f5f5f5' : 'white', borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '0.75rem', textAlign: 'left' }}>{getStatValue(team1Players, stat)}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{stat}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{getStatValue(team2Players, stat)}</td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );

      case 'whatsnext':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>🏃 What's Next</h4>
            <div style={{ fontSize: '0.95rem' }}>
              {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
              {block.opponent && <div><strong>Opponent:</strong> {getStringValue(block.opponent)}</div>}
              {block.date && <div><strong>Date:</strong> {getStringValue(block.date)}</div>}
              {block.details && <div><strong>Details:</strong> {getStringValue(block.details)}</div>}
            </div>
          </div>
        );

      case 'leaderboard':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>📈 Leaderboard Entry</h4>
            <div style={{ fontSize: '0.95rem' }}>
              {block.player && <div><strong>Player:</strong> {getStringValue(block.player)}</div>}
              {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
              {block.stat && <div><strong>Stat:</strong> {getStringValue(block.stat)}</div>}
              {block.value && <div><strong>Value:</strong> {getStringValue(block.value)}</div>}
            </div>
          </div>
        );

      case '247sports':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>⭐ 247Sports</h4>
            <div style={{ fontSize: '0.95rem' }}>
              <div><strong>Type:</strong> {block.type === 'player' ? 'Player' : 'Team'}</div>
              {block.name && <div><strong>Name:</strong> {getStringValue(block.name)}</div>}
            </div>
          </div>
        );

      case 'xfactor':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>✨ X-Factor</h4>
            <div style={{ fontSize: '0.95rem' }}>
              {block.player && <div><strong>Player:</strong> {getStringValue(block.player)}</div>}
              {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
              {block.analysis && <div><strong>Analysis:</strong> {getStringValue(block.analysis)}</div>}
            </div>
          </div>
        );

      case 'hottakes':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: `2px solid ${block.heat === 'Volcanic' ? '#ff4444' : block.heat === 'Spicy' ? '#ff9944' : block.heat === 'Medium' ? '#ffbb33' : '#88ddff'}`, borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>🔥 Hot Takes - {block.heat || 'Medium'}</h4>
            {block.content && <p style={{ margin: '0', fontSize: '0.95rem' }}>{block.content}</p>}
          </div>
        );

      case 'powerranking':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>👑 Power Ranking</h4>
            <div style={{ fontSize: '0.95rem' }}>
              <div><strong>Rank:</strong> #{getStringValue(block.rank, 'N/A')}</div>
              {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
            </div>
          </div>
        );

      case 'momentummeter':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>📊 Momentum Meter</h4>
            <div style={{ fontSize: '0.95rem' }}>
              {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span><strong>Momentum:</strong></span>
                  <div style={{ flex: 1, height: '20px', backgroundColor: '#eee', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${block.momentum || 50}%`, backgroundColor: '#4CAF50' }}></div>
                  </div>
                  <span>{block.momentum || 50}%</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'trivia':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>🧠 Trivia Question</h4>
            <div style={{ fontSize: '0.95rem' }}>
              {block.question && <div style={{ marginBottom: '0.75rem', fontWeight: 'bold' }}>{getStringValue(block.question)}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                {block.optionA && <div>A) {getStringValue(block.optionA)}</div>}
                {block.optionB && <div>B) {getStringValue(block.optionB)}</div>}
                {block.optionC && <div>C) {getStringValue(block.optionC)}</div>}
                {block.optionD && <div>D) {getStringValue(block.optionD)}</div>}
              </div>
              {block.correct && <div><strong>Correct Answer:</strong> {getStringValue(block.correct)}</div>}
              {block.explanation && <div><strong>Explanation:</strong> {getStringValue(block.explanation)}</div>}
            </div>
          </div>
        );

      case 'pickems':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>🏆 Pick 'Em</h4>
            <div style={{ fontSize: '0.95rem' }}>
              {block.team1 && block.team2 && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ padding: '0.5rem', backgroundColor: block.pick === 'team1' ? '#e3f2fd' : 'transparent', borderRadius: '4px' }}>
                    {block.pick === 'team1' && '✓ '}{getStringValue(block.team1)}
                  </div>
                  <div style={{ margin: '0.5rem 0', textAlign: 'center', color: '#999' }}>vs</div>
                  <div style={{ padding: '0.5rem', backgroundColor: block.pick === 'team2' ? '#e3f2fd' : 'transparent', borderRadius: '4px' }}>
                    {block.pick === 'team2' && '✓ '}{getStringValue(block.team2)}
                  </div>
                </div>
              )}
              {block.confidence && <div><strong>Confidence:</strong> {getStringValue(block.confidence)}</div>}
            </div>
          </div>
        );

      case 'transactions':
        return (
          <div key={block.id} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 0.75rem 0' }}>📰 Transaction</h4>
            <div style={{ fontSize: '0.95rem' }}>
              {block.type && <div><strong>Type:</strong> {String(getStringValue(block.type)).charAt(0).toUpperCase() + String(getStringValue(block.type)).slice(1)}</div>}
              {block.player && <div><strong>Player:</strong> {getStringValue(block.player)}</div>}
              {block.team && <div><strong>Team:</strong> {getStringValue(block.team)}</div>}
              {block.date && <div><strong>Date:</strong> {getStringValue(block.date)}</div>}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="article-preview">
      <div style={{ padding: '2rem' }}>
        {title && <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>{title}</h1>}
        {authorName && <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>By {authorName}</p>}
        {headerImage && (
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <img src={headerImage} alt="Header" style={{ maxWidth: '100%', height: 'auto' }} />
          </div>
        )}
        <div className="article-body">
          {blocks && blocks.length > 0 ? (
            blocks.map((block) => renderPreviewBlock(block))
          ) : (
            <p style={{ textAlign: 'center', color: '#999' }}>No content to preview</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticlePreview;
